import { Box } from "@chakra-ui/react";
import { colors, ColorIcon, Spacing } from "goi_common";
import { BaseStack } from "goi_common/src/design-system";
import { ComponentProps } from "react";
import { MUBINSO_E4_LANDING } from "./mubinsoE4Constants";
import { MUBINSO_TOPIC_OPTIONS } from "./mubinsoConstants";
import { ChevronRightIcon } from "./MubinsoUI";

/**
 * E4 개편 첫 화면. 첫 화면과 Q1(궁금한 점)을 한 화면으로 합쳤다(주완 2026-10-30).
 *
 * 첫 화면 이탈이 가장 커서, 들어오자마자 누를 곳이 보이게 한다. 예전에는 소개 카드 아래
 * 「알아보기」 버튼이 360px 화면 밖(약 825px)에 있었고, 누르면 Q1 을 한 번 더 거쳤다.
 * 이제 Q1 답 4개와 급한 고객 버튼이 첫 화면 버튼이고, 누르면 바로 해당 퍼널(또는 급한 고객 모달)로 간다.
 * 버튼 모양은 가족장 분기 화면(MubinsoFamilyBranchScreen, PR #2728)과 같다.
 */
export default function MubinsoLandingE4({
  onPickTopic,
  onUrgent,
}: {
  /** Q1 답(MUBINSO_TOPIC_OPTIONS 의 label)을 넘긴다 */
  onPickTopic: (label: string) => void;
  /** 급한 고객 버튼. 전화 연결이 아니라 번호만 받는 리드 모달을 연다 */
  onUrgent: () => void;
}) {
  return (
    <BaseStack width="100%" maxW="480px" m="0 auto" minH="100dvh" px={24}>
      <Spacing gap={40} />
      <Box width="100%" className="head2_b black">
        무빈소 장례를
        <br />
        알아보고 계신가요?
      </Box>
      <Spacing gap={12} />
      <Box width="100%" whiteSpace="pre-line" className="body_16_r gray600">
        {MUBINSO_E4_LANDING.sub}
      </Box>

      {/* 「어떤 부분이 궁금하신가요?」 머리는 두지 않는다. 버튼 문구가 곧 질문이다 */}
      <Spacing gap={28} />
      <BaseStack width="100%" spacing={8}>
        {/* 이벤트 · 결과 모달 주제는 Q1 답 문구(option.label)를 그대로 넘긴다. 버튼에 보이는 문구만 짧다 */}
        {MUBINSO_TOPIC_OPTIONS.map((option) => (
          <LandingOptionButton
            key={option.label}
            {...MUBINSO_E4_LANDING.topicButtons[option.funnel]}
            onClick={() => onPickTopic(option.label)}
          />
        ))}
      </BaseStack>

      {/* 급한 고객은 질문 선택지와 다른 길이라 간격을 더 띄운다(8 → 16px, 주완 2026-10-30).
          홈 「24시간 대기」 카드(StandBy24Hours)와 같은 파란 바탕 · 파란 글자로 구분한다. 설명 없이 한 줄 */}
      <Spacing gap={16} />
      <LandingOptionButton {...MUBINSO_E4_LANDING.urgent} urgent onClick={onUrgent} />

      <Spacing gap={40} />
    </BaseStack>
  );
}

type IconName = ComponentProps<typeof ColorIcon>["name"];

/** 아이콘 + 굵은 제목 / 회색 설명 + 오른쪽 화살표. 누르면 바로 넘어간다(선택 상태 없음) */
function LandingOptionButton({
  icon,
  title,
  desc,
  urgent,
  onClick,
}: {
  icon: IconName;
  title: string;
  desc?: string;
  urgent?: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      as="button"
      type="button"
      width="100%"
      display="flex"
      alignItems="center"
      gap="12px"
      textAlign="left"
      cursor="pointer"
      borderRadius="6px"
      border={`1px solid ${urgent ? colors.blue150 : colors.gray200}`}
      padding="14px 16px"
      bg={urgent ? colors.blue150 : colors.white}
      // 호버는 BaseButton 규칙대로 같은 색 한 단계 진하게(흰색 → gray50, blue150 → blue200)
      _hover={{ bg: urgent ? colors.blue200 : colors.gray50 }}
      onClick={onClick}
    >
      {/* 아이콘은 제목 줄에 맞춰 위에 붙인다. 설명이 1~2줄로 달라도 아이콘 · 제목 위치가 버튼마다 같다.
          화살표는 버튼 전체를 누르는 표시라 가운데 둔다 */}
      <Box flex="none" alignSelf={desc ? "flex-start" : "center"}>
        <ColorIcon name={icon} size={32} />
      </Box>
      <BaseStack flex={1} minW={0} spacing={2}>
        {/* 제목 16 → 15px: 버튼 5개의 세로를 줄인다(주완 2026-10-30). 설명 14px 보다 한 단계 크고 굵게 */}
        <Box className="body_2_normal_sb" color={urgent ? colors.blue750 : colors.gray900}>
          {title}
        </Box>
        {desc && (
          <Box className="body_14_r gray600" style={{ wordBreak: "keep-all" }}>
            {desc}
          </Box>
        )}
      </BaseStack>
      <Box flex="none" color={urgent ? colors.blue750 : colors.gray400}>
        <ChevronRightIcon />
      </Box>
    </Box>
  );
}
